'use client';

import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { Languages } from 'lucide-react';
import { useTranslations } from '@/hooks/useTranslations';
import { LanguageSettingsSchema } from '@/schemas/language';
import { languageSettings } from '@/actions/account/language-settings';
import { startTransition } from 'react';
import * as z from 'zod';
import { useRouter, usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';

const LOCALES = ['en', 'sr', 'si'] as const;
type Locale = (typeof LOCALES)[number];

export function LanguageMenu() {
  const t = useTranslations('Navigation');
  const router = useRouter();
  const pathname = usePathname();
  const { update: updateSession } = useSession();

  const changeUrlLocale = (currentPath: string, newLocale: Locale) => {
    const parts = currentPath.split('/');
    if (LOCALES.includes(parts[1] as Locale)) {
      parts[1] = newLocale;
    } else {
      parts.splice(1, 0, newLocale);
    }
    const next = parts.join('/').replace(/\/{2,}/g, '/');
    router.replace(next);
  };

  const onSubmit = (values: z.infer<typeof LanguageSettingsSchema>) => {
    startTransition(() => {
      languageSettings(values)
        .then(async (data) => {
          if (!data.success) {
            console.error('Language update failed');
            return;
          }

          const newLocale = values.language as Locale;

          // 1) Update NextAuth JWT (so session reflects new language immediately)
          await updateSession({ language: newLocale });

          // 2) Navigate to the same route with the new locale segment
          changeUrlLocale(pathname, newLocale);
        })
        .catch(console.error);
    });
  };

  const handleLanguageChange = (lang: Locale) => {
    const values: z.infer<typeof LanguageSettingsSchema> = { language: lang };
    const parsed = LanguageSettingsSchema.safeParse(values);
    if (!parsed.success) return console.warn('Invalid language value');
    onSubmit(values);
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="cursor-pointer">
        <Languages className="w-4 h-4" color="#787B80" />
        <span className="text-sm font-medium">{t('language')}</span>
      </DropdownMenuSubTrigger>

      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          <DropdownMenuItem onClick={() => handleLanguageChange('en')}>
            {t('english')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleLanguageChange('sr')}>
            {t('serbian')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleLanguageChange('si')}>
            {t('slovenian')}
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
